import React, { useState } from "react";

// 3rd Party Library
import PropTypes from "prop-types";
import {
  Accordion,
  Card,
  Col,
  OverlayTrigger,
  Popover,
  Row,
  Tooltip,
  useAccordionButton,
} from "react-bootstrap";
import { IoIosArrowDropdown, IoIosArrowDropup } from "react-icons/io";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";

// Utils
import {
  capitalizeFirstLetter,
  convertEpochToDate,
  decrypt,
} from "../../utilities/helpers.js";

// Styles
import styles from "./style.module.scss";
import { formatServerTime } from "../../utilities/dateUtils.js";

const CustomAccordion = ({
  showOverlay,
  setShowOverlay,
  OnModalOpen,
  row,
  AccordionBgClass,
  headings,
  title,
  ellipse,
  pageLink,
}) => {
  const { userName, userRole } = useSelector(
    (state) => state.common.loggedUser
  );
  const isDataPresent = !!row;
  const navigate = useNavigate();
  const cookies = new Cookies();
  const encoded = decrypt(userName);
  const [isExpanded, setIsExpanded] = useState(false);
  const policyNameHeading = headings.find(
    (heading) => heading.value === "policyName"
  );
  const level = headings.find(
    (heading) => heading.value === "levelOfTheAssessment"
  );
  const appNameHeading = headings.find(
    (heading) => heading.value === "appName"
  );
  const appOwners =
    headings.find((heading) => heading.value === "applicationManagers") ?? [];
  const userReviewStatus = headings.find(
    (heading) => heading.value === "isUserSubmittedForReview"
  );
  const aoReviewStatus = headings.find(
    (heading) => heading.value === "isAOsubmittedForApproval"
  );
  const cisoReviewStatus = headings.find(
    (heading) => heading.value === "isCISOsubmittedForCompletion"
  );
  const aoRecheckStatus = headings.find(
    (heading) => heading.value === "isAoRecheckToUser"
  );
  const cisoRecheckStatus = headings.find(
    (heading) => heading.value === "isCisoRecheckToAoAndUser"
  );
  const allowedUsers = headings.find(
    (heading) => heading.value === "UsersForThisAssessment"
  );
  const nonCompliantItems = headings.find(
    (heading) => heading.value === "nonCompliantItems"
  );
  const compliantItems = headings.find(
    (heading) => heading.value === "compliantItems"
  );
  const partiallyCompliantItems = headings.find(
    (heading) => heading.value === "partiallyCompliantItems"
  );
  const notApplicableItems = headings.find(
    (heading) => heading.value === "notApplicableItems"
  );
  const userCompletedStatus = headings.find((heading) => heading.percentage);

  const hasAssessmentStarted =
    row[nonCompliantItems.value] > 0 ||
    row[compliantItems.value] > 0 ||
    row[partiallyCompliantItems.value] > 0 ||
    row[notApplicableItems.value] > 0;

  // Define the CustomToggle component and event handlers conditionally

  const CustomToggle = ({ children, eventKey }) => {
    const decoratedOnClick = useAccordionButton(eventKey, () =>
      setIsExpanded(!isExpanded)
    );

    if (!isDataPresent) {
      return <div>{children}</div>;
    }
    return (
      <a onClick={decoratedOnClick} data-testid="accordion-item-link">
        <span>{children}</span>
      </a>
    );
  };

  const handleStartAssessment = (row) => {
    cookies.remove("assessmentstatus");
    cookies.set("assessmentstatus", 0); // Set cookie for 'start' assessment
    const navState = JSON.stringify({
      assessmentId: row?.assessmentId,
      policyName: row?.policyName,
    });
    localStorage.setItem("raNavState", navState);
    navigate(pageLink);
  };

  const handleViewAssessment = (row) => {
    cookies.remove("assessmentstatus");
    cookies.set("assessmentstatus", 2); // Set cookie for 'view' assessment
    const navState = JSON.stringify({
      assessmentId: row?.assessmentId,
      policyName: row?.policyName,
    });
    localStorage.setItem("raNavState", navState);
    navigate(pageLink);
  };

  const handleReviewAssessment = (row) => {
    cookies.remove("assessmentstatus");
    cookies.set("assessmentstatus", 1); // Set cookie for 'review' assessment
    const navState = JSON.stringify({
      assessmentId: row?.assessmentId,
      policyName: row?.policyName,
    });
    localStorage.setItem("raNavState", navState);
    navigate(pageLink);
  };

  const popoverTop = (row) => {
    const completedPercent = row[userCompletedStatus?.value];
    const isAssessmentCompletedByUser = row[userReviewStatus?.value] === true;
    const isAssessmentReviewedByAo = row[aoReviewStatus?.value] === true;
    const isAssessmentReviewedByCiso = row[cisoReviewStatus?.value] === true;
    const isAoRequestedRecheck = row[aoRecheckStatus?.value] === true;
    const isCisoRequestedRecheck = row[cisoRecheckStatus?.value] === true;

    return (
      <Popover id="popover-positioned-top">
        <ul className="p-1">
          {row.startAssessment && userRole.includes("User") && (
            <li className="p-1">
              <a
                onClick={() => {
                  handleStartAssessment(row);
                }}
              >
                Start Assessment
              </a>
            </li>
          )}
          {userRole.includes("Operation") && (
            <li className="p-1">
              <a onClick={() => OnModalOpen(title)} data-testid="accordion-delete-assessment-link">Delete Assessment</a>
            </li>
          )}
          {((userRole.includes("Application Manager") && row.reviewByAO) ||
            (userRole.includes("Ciso") && row.reviewByCISO)) && (
            <li className="p-1">
              <a
                onClick={() => {
                  handleReviewAssessment(row);
                }}
              >
                Review Assessment
              </a>
            </li>
          )}

          {row?.viewOnly && (
            <li className="p-1">
              <a
                onClick={() => {
                  handleViewAssessment(row);
                }}
              >
                View Assessment
              </a>
            </li>
          )}
        </ul>
      </Popover>
    );
  };

  return isDataPresent ? (
    <>
      <Card className={`p-3 border-0 ${AccordionBgClass}`}>
        <Accordion defaultActiveKey="">
          <div key={row.id} className="">
            <CustomToggle eventKey={row.id}>
              <div
                className={`d-flex justify-content-between ${
                  isExpanded ? "expanded-toggle-bg" : ""
                }`}
              >
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div className="align-items-start">
                    <span className="mr-5">
                      {isExpanded ? (
                        <IoIosArrowDropup size={20} color="grey" />
                      ) : (
                        <IoIosArrowDropdown size={20} color="grey" />
                      )}
                    </span>
                  </div>
                  <Row className="w-100 ms-3">
                    <Col xs={12} md={3} className="text-truncate">
                      {title}
                    </Col>
                    <Col xs={12} md={3} className="text-truncate">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip className="tooltipHeader px-3">
                            {row[appNameHeading?.value]}
                          </Tooltip>
                        }
                      >
                        <span>
                          {" "}
                          <strong>{appNameHeading?.label}: </strong>
                          {row[appNameHeading?.value]}
                        </span>
                      </OverlayTrigger>
                    </Col>
                    <Col xs={12} md={3} className="text-truncate">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip className="tooltipHeader px-3">
                            {row[policyNameHeading?.value]}
                          </Tooltip>
                        }
                      >
                        <span>
                          {" "}
                          <strong>{policyNameHeading?.label}: </strong>
                          {row[policyNameHeading?.value]}
                        </span>
                      </OverlayTrigger>
                    </Col>
                    <Col xs={12} md={3}>
                      <strong>{level?.label}: </strong>
                      {row[level?.value] && row[level?.value].length > 0
                        ? row[level?.value][
                            row[level?.value].length - 1
                          ]?.replace("Level ", "")
                        : "-"}
                    </Col>
                  </Row>
                </div>
                <div id="ellipseAction">
                  {ellipse ? (
                    <div
                      id="ellipseContainer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <OverlayTrigger
                        rootClose
                        rootCloseEvent="mousedown"
                        placement="top"
                        overlay={popoverTop(row)}
                        show={showOverlay === row.assessmentId}
                      >
                        <div
                          className={`${styles.ellipsisStyle} mr-10`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowOverlay((prev) =>
                              prev === row.assessmentId
                                ? null
                                : row.assessmentId
                            );
                          }}
                        >
                          <a>&#8943;</a>
                        </div>
                      </OverlayTrigger>
                    </div>
                  ) : null}
                </div>
              </div>
            </CustomToggle>
            <Accordion.Collapse
              eventKey={row.id}
              className="detailsAssessment"
              onEnter={() => setIsExpanded(true)}
              onExit={() => setIsExpanded(false)}
            >
              <div className="p-3 mt-3 assessmentcontent">
                <Row>
                  {headings.slice(1).map(
                    (heading, index) =>
                      heading.hideField !== true &&
                      heading.label !== "Policy" &&
                      heading.label !== "Application" && (
                        <Col key={index} xxl={4} xl={4} lg={4} md={12}>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <div className="mb-2">
                              <strong>{heading.label}:</strong>
                            </div>
                          </td>
                          <td>
                            {heading.epoc
                              ? row[heading.value] === 0
                                ? " - "
                                : Array.isArray(row[heading.value]) &&
                                  row[heading.value].length === 0
                                ? " - "
                                : row[heading.value] === "Not Submitted" ||
                                  row[heading.value] === "N/A"
                                ? row[heading.value]
                                : Array.isArray(row[heading.value])
                                ? row[heading.value]
                                    .map((date) => formatServerTime(date, { includeTime: true }))
                                    .join(", ")
                                : formatServerTime(row[heading.value], { includeTime: true })
                              : Array.isArray(row[heading.value])
                              ? row[heading.value]
                                  .map((Item) => capitalizeFirstLetter(Item))
                                  .join(", ")
                              : row[heading.value]}
                            {heading.percentage ? "%" : ""}
                          </td>
                        </Col>
                      )
                  )}
                </Row>
              </div>
            </Accordion.Collapse>
          </div>
        </Accordion>
      </Card>
    </>
  ) : (
    <div className={`${styles.noDataAvail} text-center`}>No data found.</div>
  );
};

CustomAccordion.propTypes = {
  children: PropTypes.node.isRequired, // required if the component must have content
  eventKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  showOverlay: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.null,
  ]),
  setShowOverlay: PropTypes.func.isRequired,
  OnModalOpen: PropTypes.func.isRequired,
  row: PropTypes.object,
  AccordionBgClass: PropTypes.string,
  headings: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      hideField: PropTypes.bool,
      epoc: PropTypes.bool,
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
  ellipse: PropTypes.bool,
  pageLink: PropTypes.string.isRequired,
};
CustomAccordion.defaultProps = {
  showOverlay: null,
  row: {},
  AccordionBgClass: "",
  ellipse: false,
  barColumn: "",
};

export default React.memo(CustomAccordion);
